import React from "react";
import { Check, Play } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ScrollReveal from "../components/site/ScrollReveal";
import {
  PlannerBlock, MelbourneSection, FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import InternalRail from "../components/site/InternalRail";
import { blogsForService } from "../data/internalLinks";

export default function RestaurantReels() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <WhyMatters />
      <ReelGrid />
      <Categories />
      <PlannerBlock
        eyebrow="Plan it now · Indicative scope"
        headline="Cost a reels day"
        italic="in under a minute."
        blurb="Slide for menu size, tick reels and we'll add it to the planner. Most reels add-on days deliver 3–6 vertical clips alongside the photo shoot."
      />
      <MelbourneSection
        serviceLabel="Restaurant Reels"
        eyebrow="Melbourne hospitality · Mobile coverage"
        headline="A reels producer for"
        italic="Melbourne kitchens."
        intro="Restaurant reels and short-form video for venues across Greater Melbourne — from cocktail bars in the CBD to busy curry kitchens in Footscray, brunch cafés in Carlton and family restaurants in Glen Waverley and Dandenong."
      />
      <SisterServices exclude="reels" keys={["menu", "food", "brand", "cafe"]} />
      <InternalRail
        eyebrow="Related reading · The Journal"
        headline="More on short-form"
        italic="and marketing."
        theme="ink"
        cols={3}
        items={blogsForService("restaurant-reels")}
      />
      <FAQSection
        eyebrow="Common questions · Restaurant reels Melbourne"
        headline="Format, music,"
        italic="and turnaround."
        intro="Reels are typically scoped as an add-on to a photo day. If you want a dedicated video day with a longer brief, mention it on the call — we'll structure it differently."
        faqs={FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Ready to shoot"
        italic="reels that travel?"
        intro="Quick form below or call directly. For reels we'll usually do a 10-minute call to scope dishes, music feel and platform spec before quoting."
        backdropSlug="menu-falooda"
        formTitle="Tell us about your reels brief."
      />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="menu-falooda" alt="Restaurant reels Melbourne — beverage pour" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/40 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[100svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Restaurant Reels Melbourne" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Restaurant Reels Melbourne · Instagram · TikTok · Shorts
        </p>

        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7.5vw, 6.5rem)" }}>
          Reels that
          <br />
          actually <span className="italic font-light text-ember">travel.</span>
        </h1>

        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          Short-form video for Melbourne restaurants, cafés and bars. Pours,
          plating, kitchen action, service moments, dish reveals — shot
          vertical, edited tight, music-cleared and ready to post the same
          week. Add to a photo day or book as a dedicated production.
        </p>

        <p className="mt-7 max-w-xl">
          <span className="inline-block bg-ember text-bone font-bold text-sm md:text-base uppercase tracking-wider2 px-4 py-2.5">
            Reels Add-on From $350
          </span>
        </p>
        <p className="mt-3 text-xs text-bone/55 max-w-md leading-relaxed">
          Bolted onto a photo day — 3–6 finished vertical clips, music-cleared,
          captioned. Dedicated reels days from $900 (8–12 clips).
        </p>

        <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
          <a href="#enquiry" className="w-full sm:w-auto">
            <CTAButton variant="primary" size="lg" className="w-full sm:w-auto">Get A Reels Quote</CTAButton>
          </a>
          <a href="#planner" className="w-full sm:w-auto">
            <CTAButton variant="ghost" size="lg" arrow={false} className="w-full sm:w-auto">Plan The Shoot</CTAButton>
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-12 grid grid-cols-3 gap-4 md:gap-10 max-w-3xl border-t border-bone/15 pt-6">
        {[
          ["Format", "Vertical · 9:16"],
          ["Turnaround", "5–7 days"],
          ["Per shoot", "3–12 clips"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider2 text-ember mb-1.5 font-bold">{k}</p>
            <p className="font-sans font-bold text-base sm:text-lg md:text-xl leading-tight tracking-tight">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyMatters = () => (
  <section className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Why reels travel further than photos
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Instagram favours
            <br />
            <span className="italic font-light text-ember">the moving frame.</span>
          </h2>

          <div className="mt-12 grid gap-7 text-bone/85 leading-relaxed text-base md:text-lg">
            <ScrollReveal>
              <p>
                Reels and short-form video sit at the top of the Instagram and
                TikTok algorithm — a single well-shot 15-second clip
                <strong className="text-bone"> reaches 5–20× more people than a still photo</strong> from the
                same account. For hospitality, that means walk-in, that means
                bookings, and increasingly that means delivery orders.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={120}>
              <p>
                We shoot vertical-first, tight, with motion that reads at
                arm's length — pours that catch the light, knife cuts,
                pull-apart food, cocktail builds, candid service moments.
                Real kitchens, real plates, no plastic styling.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p>
                Music is cleared, captions are clean, hooks land in the first
                two seconds. Files arrive ready to post — no extra editing
                required from your marketing team.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={280}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-4">
                {[
                  "Vertical 9:16 native crop",
                  "Hooks in first 2 seconds",
                  "Music-cleared library",
                  "On-screen captions",
                  "Instagram / TikTok / Shorts",
                  "Ready to post — no extra edit",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check size={16} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 space-y-3 md:space-y-5 lg:mt-4">
          <div className="relative">
            <Photo slug="menu-falooda" alt="Restaurant reels Melbourne — beverage pour" ratio="9/16" sizes="(min-width: 1024px) 30vw, 100vw" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="w-16 h-16 rounded-full bg-ember/95 flex items-center justify-center shadow-2xl">
                <Play size={26} strokeWidth={2} className="text-bone ml-1" fill="currentColor" />
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            <Photo slug="hero-laddu-220" alt="Restaurant reels Melbourne — dish pour" ratio="9/16" sizes="15vw" />
            <Photo slug="menu-malai-tikka" alt="Restaurant reels Melbourne — plating" ratio="9/16" sizes="15vw" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ReelGrid = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-6">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            The reel feed · Vertical-first
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Six clips,
            <br />
            <span className="italic font-light text-ember">one shoot day.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed">
            A typical reels add-on delivers six finished vertical clips — a
            mix of dish hero pours, kitchen action, service moments and
            brand. Enough content for a whole month of organic posting and
            two paid sets.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {["menu-falooda", "hero-laddu-220", "menu-malai-tikka", "menu-butter-chicken", "menu-chinese-bhel-03", "hero-banquet-01"].map((s) => (
          <div key={s} className="relative">
            <Photo slug={s} alt={`Restaurant reels Melbourne — ${s}`} ratio="9/16" sizes="(min-width: 1024px) 15vw, 33vw" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-ink/70 backdrop-blur-sm flex items-center justify-center">
                <Play size={16} strokeWidth={2} className="text-bone ml-0.5" fill="currentColor" />
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CATEGORIES = [
  { n: "01", title: "Dish hero pours", slug: "menu-falooda", desc: "The classic — sauce ladled over biryani, cream poured into curry, syrup catch on dessert. The single highest-converting reel format." },
  { n: "02", title: "Knife & cut", slug: "menu-malai-tikka", desc: "Cuts through stack, slice through bread, pull-apart pieces. Texture-led reels that earn the scroll-stop." },
  { n: "03", title: "Cocktail builds", slug: "menu-falooda", desc: "Bar action — pour, stir, garnish, smoke, flame. The reel format every cocktail-led venue should own." },
  { n: "04", title: "Kitchen action", slug: "hero-banquet-01", desc: "Pass plating, wok hei, tandoor flames, chef hands. Real working kitchens — the most authentic reel a hospitality account can post." },
  { n: "05", title: "Service moments", slug: "venue-buffet-01", desc: "A table being set, a plate being delivered, hands on the table. Atmospheric reels that sell the room, not just the food." },
  { n: "06", title: "Hooks & teasers", slug: "menu-butter-chicken", desc: "Question-led reels for new menu items and limited releases — the format that drives bookings and DMs." },
  { n: "07", title: "Day-in-life", slug: "hero-banquet-01", desc: "Open to close, multi-clip narrative reels showing your venue's full day. Long-form reach, evergreen content." },
  { n: "08", title: "Reels for paid", slug: "hero-spread-01", desc: "Built specifically for Meta and TikTok paid sets — captioned, hooked, music-cleared, hard cut to 15 seconds." },
];

const Categories = () => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-14 lg:mb-20 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            What we shoot · Reel formats
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Eight reel formats.
            <br />
            <span className="italic font-light text-ember">All in one day.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            One reels day can cover hero pours, knife cuts, kitchen action and
            service moments — enough variety to keep your social calendar
            posting for a month.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
        {CATEGORIES.map((c) => (
          <article key={c.n} className="bg-ink hover:bg-[#161613] transition-colors p-5 md:p-6 flex flex-col">
            <Photo slug={c.slug} alt={`${c.title} — Restaurant reels Melbourne`} ratio="4/5" sizes="(min-width: 1024px) 22vw, 50vw" />
            <div className="mt-5 flex flex-col flex-1">
              <span className="font-display italic text-2xl text-ember leading-none">{c.n}</span>
              <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-2.5">{c.title}</h3>
              <p className="mt-3 text-sm text-bone/70 leading-relaxed flex-1">{c.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const FAQS = [
  { q: "How long are the reels and what format do you deliver?", a: "Finished clips are 8–20 seconds, native 9:16 vertical, MP4 ready to upload directly to Instagram, TikTok and YouTube Shorts. Captions and hook text are baked in. We can also deliver clean (no caption) masters for paid use." },
  { q: "Do you write the captions and choose music?", a: "Yes — clips are delivered with on-screen captions, hook text and music-cleared backing. If you have a brand-approved music library or specific captions you want, share them on the call and we'll work with that." },
  { q: "How many reels do we get per shoot day?", a: "A reels add-on (bolted to a photo day) typically delivers 3–6 finished clips. A dedicated reels day produces 8–12 finished clips covering multiple formats — hero pours, kitchen action, service, plus a couple of paid-ad-ready cuts." },
  { q: "Can the reels be used for paid Meta and TikTok ads?", a: "Yes. All clips include a clean, caption-free version for paid use, plus the on-screen captioned version for organic. Music is licensed for paid use through commercial libraries — never trending TikTok tracks that would get takedowns on Meta." },
  { q: "How much do restaurant reels cost in Melbourne?", a: "Reels add-ons start at $350 when bolted to a photo day (3–6 finished clips). Dedicated reels production days from $900 (8–12 clips). Monthly retainers (4 reels/month) from $700/month." },
  { q: "How fast is turnaround?", a: "Standard delivery is 5–7 business days. Urgent 72-hour turnarounds available for campaign launches — flag the deadline on the call." },
];
